export enum WidgetType {
  Accordion = 'Accordion',
  Audio = 'Audio',
  BlockTitle = 'BlockTitle',
  Carousel = 'Carousel',
  Card = 'Card',
  CardSection = 'CardSection',
  ClozeQuestion = 'ClozeQuestion',
  CombinedTitle = 'CombinedTitle',
  ContentArea = 'ContentArea',
  Embed = 'Embed',
  EnclosureWithBlockTitle = 'EnclosureWithBlockTitle',
  EnclosureWithCombinedTitle = 'EnclosureWithCombinedTitle',
  EvaluatablePlugin = 'EvaluatablePlugin',
  FillInTheBlanksQuestion = 'FillInTheBlanksQuestion',
  FlexSection = 'FlexSection',
  Geogebra = 'Geogebra',
  GeogebraQuestion = 'GeogebraQuestion',
  Hotspot = 'Hotspot',
  HotspotQuestion = 'HotspotQuestion',
  Image = 'Image',
  InteractiveVideo = 'InteractiveVideo',
  InteractivePoll = 'InteractivePoll',
  InteractiveQuiz = 'InteractiveQuiz',
  MicrosoftEmbed = 'MicrosoftEmbed',
  OpenDiscussion = 'OpenDiscussion',
  OpenQuestion = 'OpenQuestion',
  Page = 'Page',
  PdfDocument = 'PdfDocument',
  Plugin = 'Plugin',
  QuestionGroupList = 'QuestionGroupList',
  Section = 'Section',
  SelectionQuestion = 'SelectionQuestion',
  Slide = 'Slide',
  SortQuestion = 'SortQuestion',
  Spacer = 'Spacer',
  StyledBulletList = 'StyledBulletList',
  Table = 'Table',
  Text = 'Text',
  UnifiedVideo = 'UnifiedVideo',
  Video = 'Video',
  YesNoQuestion = 'YesNoQuestion'
}

export enum EvaluationMode {
  Auto = 'auto',
  Manual = 'manual'
}

export const evaluatableWidgets = [
  WidgetType.SelectionQuestion,
  WidgetType.SortQuestion,
  WidgetType.ClozeQuestion,
  WidgetType.FillInTheBlanksQuestion,
  WidgetType.GeogebraQuestion,
  WidgetType.HotspotQuestion,
  WidgetType.EvaluatablePlugin,
  WidgetType.QuestionGroupList,
  WidgetType.InteractiveQuiz,
  WidgetType.YesNoQuestion
] as const;

export const flexibleWidgets = [
  WidgetType.FlexSection,
  WidgetType.Card,
  WidgetType.CardSection,
  WidgetType.SelectionQuestion,
  WidgetType.ClozeQuestion,
  WidgetType.OpenQuestion,
  WidgetType.FillInTheBlanksQuestion,
  WidgetType.GeogebraQuestion,
  WidgetType.InteractivePoll,
  WidgetType.InteractiveQuiz,
  WidgetType.QuestionGroupList,
  WidgetType.SortQuestion,
  WidgetType.EvaluatablePlugin
] as const;

export const isFlexibleWidget = (widgetType: unknown) => {
  return flexibleWidgets.includes(widgetType as (typeof flexibleWidgets)[number]);
};

// Widgets whose nested content is found by recursing directly into their `children`
// (excludes WidgetType.Section, which needs its own layout-column sort before recursing).
export const containerWidgets = [
  WidgetType.FlexSection,
  WidgetType.Card,
  WidgetType.CardSection,
  WidgetType.EnclosureWithBlockTitle,
  WidgetType.EnclosureWithCombinedTitle,
  WidgetType.Accordion
] as const;

export const isContainerWidget = (widgetType: unknown) => {
  return containerWidgets.includes(widgetType as (typeof containerWidgets)[number]);
};
// TODO: Make it possibe to evaluate OpenQuestion
export const scorableWidgets = [...evaluatableWidgets, WidgetType.OpenQuestion];

// Scorable questions that aren't auto-evaluated — their score is assigned manually by a teacher.
export const manuallyGradedQuestionWidgets = scorableWidgets.filter(
  widget => !evaluatableWidgets.includes(widget as (typeof evaluatableWidgets)[number])
);

/**
 * The widgets that report their OWN id: the Studio "Identifiers" panel lists exactly these, and
 * `data-agent-widget-id` carries the widget's own id for exactly these. Every scorable question EXCEPT the
 * `QuestionGroupList` container — a group is descended into and its sub-questions listed individually, since
 * they are what report their own answers.
 *
 * Keep in step with {@link isMediaEventWidget}: the two together are the widgets that report at all, and a
 * widget must never be in both.
 */
export const reportableQuestionWidgets: ReadonlySet<string> = new Set<string>(
  scorableWidgets.filter(widget => widget !== WidgetType.QuestionGroupList)
);

export const isReportableQuestionWidget = (widgetType: unknown): boolean =>
  reportableQuestionWidgets.has(widgetType as string);

/**
 * The widgets that emit media events (`useMediaEvents`), directly or through `EmbedIframe`. MOE takes
 * `played`/`paused` at the ITEM level, so these report their PAGE's id and never their own.
 */
const mediaEventWidgets: ReadonlySet<string> = new Set<string>([
  WidgetType.Video,
  WidgetType.UnifiedVideo,
  WidgetType.InteractiveVideo,
  WidgetType.Audio,
  WidgetType.Embed,
  WidgetType.PdfDocument
]);

export const isMediaEventWidget = (widgetType: unknown): boolean => mediaEventWidgets.has(widgetType as string);

export const questionWidgets = [
  // Note: When extending, include the name in the QuestionGroupList -> QuestionBank -> locales
  WidgetType.ClozeQuestion,
  WidgetType.EvaluatablePlugin,
  WidgetType.FillInTheBlanksQuestion,
  WidgetType.GeogebraQuestion,
  WidgetType.HotspotQuestion,
  WidgetType.OpenQuestion,
  WidgetType.SelectionQuestion,
  WidgetType.SortQuestion,
  WidgetType.YesNoQuestion
];

export const collaborativeWidgets = [WidgetType.InteractivePoll, WidgetType.InteractiveQuiz, WidgetType.OpenDiscussion];

export const fullPageItems = [WidgetType.OpenDiscussion];

export const uniqueItemsPerPage = [WidgetType.OpenDiscussion, WidgetType.InteractivePoll, WidgetType.InteractiveQuiz];

export type EvaluatableWidgets = (typeof evaluatableWidgets)[number];

const isInArray = <T, A extends T>(item: T, array: ReadonlyArray<A>): item is A => array.includes(item as A);

export const canHaveScoreWeight = (widgetType: unknown) => {
  return isInArray(widgetType, scorableWidgets);
};

export const widgetsWithConfig = [
  WidgetType.Accordion,
  WidgetType.Audio,
  WidgetType.BlockTitle,
  WidgetType.Card,
  WidgetType.Carousel,
  WidgetType.ClozeQuestion,
  WidgetType.CombinedTitle,
  WidgetType.Embed,
  WidgetType.EnclosureWithBlockTitle,
  WidgetType.EnclosureWithCombinedTitle,
  WidgetType.EvaluatablePlugin,
  WidgetType.FillInTheBlanksQuestion,
  WidgetType.Geogebra,
  WidgetType.GeogebraQuestion,
  WidgetType.Hotspot,
  WidgetType.HotspotQuestion,
  WidgetType.Image,
  WidgetType.InteractivePoll,
  WidgetType.InteractiveQuiz,
  WidgetType.InteractiveVideo,
  WidgetType.MicrosoftEmbed,
  WidgetType.OpenQuestion,
  WidgetType.PdfDocument,
  WidgetType.Plugin,
  WidgetType.QuestionGroupList,
  WidgetType.SelectionQuestion,
  WidgetType.SortQuestion,
  WidgetType.StyledBulletList,
  WidgetType.Table,
  WidgetType.Text,
  WidgetType.Video
];
